import { useState } from 'react'
import type { TeamRow } from '../../pages/admin/teamStore'

/**
 * Closes a team after a last day, today by default; the team stays open
 * through that day. A closed team drops out of the
 * pickers. It cannot close while employees are still placed on it past that
 * date, so they are transferred first; the server says how many when it
 * refuses.
 */
export default function CloseTeamForm({
  team,
  today,
  saving,
  error,
  onSave,
  onCancel,
}: {
  team: TeamRow
  today: string
  saving: boolean
  error: string | null
  onSave: (closeDate: string) => void
  onCancel: () => void
}) {
  const [closeDate, setCloseDate] = useState(today > team.openDate ? today : team.openDate)

  const canSave = closeDate !== '' && closeDate >= team.openDate

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={`Close ${team.name}`}
      onSubmit={(event) => {
        event.preventDefault()
        if (canSave && !saving) onSave(closeDate)
      }}
    >
      <p className="text-small">
        Closing <span className="font-bold">{team.name}</span> takes it out of the team pickers. Employees still placed on it must be transferred first.
      </p>
      <div className="max-w-xs">
        <label htmlFor="close-team-date" className="mb-1 block text-caption font-bold text-ink-muted">
          Last day open
        </label>
        <input
          id="close-team-date"
          type="date"
          min={team.openDate}
          aria-describedby="close-team-hint"
          value={closeDate}
          onChange={(event) => setCloseDate(event.target.value)}
          className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
        />
        <p id="close-team-hint" className="mt-1 text-caption text-ink-subtle">
          The team stays open through this date.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Closing…' : 'Close team'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
